import { type KeyboardEvent, useId } from "react";
import { Link } from "wouter";
import { cx } from "./cx.js";
import { Icon } from "./Icon.jsx";

export interface TabItem {
  id: string;
  label: string;
  icon?: string;
  count?: number;
  /** Present = a navigation tab (a link with aria-current); absent = an in-page tab (role=tab). */
  href?: string;
}

const TAB =
  "inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-2.5 text-sm no-underline focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[-2px] focus-visible:outline-brand";
const ON = "border-brand font-semibold text-brand";
const OFF = "border-transparent font-medium text-ink-muted hover:text-ink";

/**
 * Section tabs. Link tabs navigate between routes; button tabs switch an in-page panel and follow the ARIA tabs
 * pattern (roving tabindex, Left/Right/Home/End). The panel is the caller's: give it id `${tabId(item)}` per `panelId`.
 */
export function Tabs({
  items,
  value,
  onChange,
  label = "Sections",
  bare,
}: {
  items: TabItem[];
  value: string;
  onChange?: (id: string) => void;
  label?: string;
  /** No bottom rule: the caller's container draws it (so it can run edge to edge). */
  bare?: boolean;
}) {
  const rule = bare ? "" : "border-b border-line";
  const base = useId();
  const content = (t: TabItem) => (
    <>
      {t.icon ? <Icon name={t.icon} size={18} /> : null}
      {t.label}
      {t.count != null ? (
        <span className="rounded-full bg-subtle px-1.5 font-mono text-xs text-ink-muted">{t.count}</span>
      ) : null}
    </>
  );
  if (items.every((t) => t.href)) {
    return (
      <nav aria-label={label} className={cx("flex gap-4 overflow-x-auto", rule)}>
        {items.map((t) => (
          <Link
            key={t.id}
            href={t.href as string}
            aria-current={t.id === value ? "page" : undefined}
            className={cx(TAB, t.id === value ? ON : OFF)}
          >
            {content(t)}
          </Link>
        ))}
      </nav>
    );
  }
  const move = (e: KeyboardEvent, i: number) => {
    const to =
      e.key === "ArrowRight"
        ? (i + 1) % items.length
        : e.key === "ArrowLeft"
          ? (i - 1 + items.length) % items.length
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? items.length - 1
              : -1;
    if (to < 0) return;
    e.preventDefault();
    onChange?.((items[to] as TabItem).id);
    document.getElementById(`${base}-tab-${(items[to] as TabItem).id}`)?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className={cx("flex gap-4 overflow-x-auto", rule)}>
      {items.map((t, i) => (
        <button
          key={t.id}
          id={`${base}-tab-${t.id}`}
          type="button"
          role="tab"
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange?.(t.id)}
          onKeyDown={(e) => move(e, i)}
          className={cx(TAB, "bg-transparent", t.id === value ? ON : OFF)}
        >
          {content(t)}
        </button>
      ))}
    </div>
  );
}
